/**
 * Raccourcis clavier globaux pour la messagerie chat (style Facebook Messenger).
 *
 * Écoute les touches au niveau `window`, mais respecte le focus dans les
 * inputs / textareas / contentEditable (l'utilisateur peut taper Esc dans le
 * message sans fermer sa conv — il faut qu'il perde d'abord le focus).
 *
 * Raccourcis :
 *   Esc            → ferme le drawer focused (si l'user ne tape pas)
 *                   → si aucun drawer focused mais search open : ferme la search
 *   Ctrl+K / Cmd+K → ouvre la recherche globale conversations
 *   Alt+←          → focus le drawer précédent
 *   Alt+→          → focus le drawer suivant
 *   Alt+M          → minimise le drawer focused
 *   Ctrl+Shift+Q   → ferme TOUS les chats ouverts
 */
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store';
import {
  closeFocused, focusNext, focusPrevious, closeAll,
  toggleMinimize, setSearchOpen,
} from '../store/slices/openChatsSlice';

function isTypingTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((el as any).isContentEditable) return true;
  return false;
}

export function useChatShortcuts() {
  const dispatch = useDispatch();
  const focusedConvId = useSelector((s: RootState) => s.openChats.focusedConvId);
  const searchOpen = useSelector((s: RootState) => s.openChats.searchOpen);
  const chatsCount = useSelector((s: RootState) => s.openChats.chats.length);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const typing = isTypingTarget(e.target);

      // Esc : priorité 1 = fermer une modale de recherche (même si typing)
      if (e.key === 'Escape') {
        if (searchOpen) {
          dispatch(setSearchOpen(false));
          e.preventDefault();
          return;
        }
        // Sinon : fermer le drawer focused SEULEMENT si l'user ne tape pas.
        // Si typing, on laisse le blur natif du navigateur agir (2e Esc fermera).
        if (!typing && focusedConvId) {
          dispatch(closeFocused());
          e.preventDefault();
        }
        return;
      }

      // Ctrl+K / Cmd+K : ouvre la recherche (empêche l'auto-recherche navigateur)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k' && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        dispatch(setSearchOpen(true));
        return;
      }

      // Ctrl+Shift+Q : ferme tous les chats
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'q') {
        e.preventDefault();
        dispatch(closeAll());
        return;
      }

      // Alt+← / Alt+→ : navigation entre chats ouverts
      if (e.altKey && !e.ctrlKey && !e.metaKey) {
        if (e.key === 'ArrowLeft' && chatsCount > 1) {
          e.preventDefault();
          dispatch(focusPrevious());
          return;
        }
        if (e.key === 'ArrowRight' && chatsCount > 1) {
          e.preventDefault();
          dispatch(focusNext());
          return;
        }
        // Alt+M : minimise le drawer focused
        if (e.key.toLowerCase() === 'm' && focusedConvId) {
          e.preventDefault();
          dispatch(toggleMinimize(focusedConvId));
          return;
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dispatch, focusedConvId, searchOpen, chatsCount]);
}
